import { createWithEqualityFn } from 'zustand/traditional';
import { persist } from 'zustand/middleware';

export interface MenuItemOrder {
  key: string;
  order: number;
  hidden?: boolean;
}

interface MenuState {
  // Порядок пунктов меню
  menuOrder: MenuItemOrder[];
  // Открытые/закрытые группы меню
  openKeys: string[];
  // Скрытые пункты меню
  hiddenItems: string[];
  // Режим редактирования (включает drag-and-drop)
  editMode: boolean;
  
  // Actions
  setMenuOrder: (order: MenuItemOrder[]) => void;
  setOpenKeys: (keys: string[] | ((prev: string[]) => string[])) => void;
  toggleEditMode: () => void;
  hideMenuItem: (key: string) => void;
  showMenuItem: (key: string) => void;
  resetMenuSettings: () => void;
  reorderMenuItem: (fromIndex: number, toIndex: number) => void;
  // Загрузить настройки меню с backend
  loadMenuSettingsFromServer: (order: MenuItemOrder[]) => void;
}

const defaultMenuOrder: MenuItemOrder[] = [
  { key: '/dashboard', order: 0 },
  { key: 'customers', order: 1 },
  { key: 'tariffs', order: 2 },
  { key: 'messages', order: 3 },
  { key: 'accounting', order: 4 },
  { key: 'support', order: 5 },
  { key: 'nodes', order: 6 },
  { key: 'cable', order: 7 },
  { key: 'devices', order: 8 },
  { key: 'tasks', order: 9 },
  { key: 'warehouse', order: 10 },
  { key: 'map', order: 11 },
  { key: 'employees', order: 12 },
  { key: 'transport', order: 13 },
  { key: 'advertisement', order: 14 },
  { key: 'notepad', order: 15 },
  { key: 'statistics', order: 16 },
  { key: 'users', order: 17 },
  { key: 'settings', order: 18 },
  { key: '/sms/send', order: 19 },
];

export const useMenuStore = createWithEqualityFn<MenuState>()(
  persist(
    (set) => ({
      menuOrder: defaultMenuOrder,
      openKeys: [],
      hiddenItems: [],
      editMode: false,

      setMenuOrder: (order) => set({ menuOrder: order }),

      setOpenKeys: (keys) => set((state) => {
        // Поддержка функционального обновления и защита от undefined
        const newKeys = typeof keys === 'function' ? keys(state.openKeys ?? []) : keys;
        return { openKeys: newKeys ?? [] };
      }),

      toggleEditMode: () => set((state) => ({ editMode: !state.editMode })),

      hideMenuItem: (key) =>
        set((state) => ({
          hiddenItems: [...state.hiddenItems, key],
        })),

      showMenuItem: (key) =>
        set((state) => ({
          hiddenItems: state.hiddenItems.filter((item) => item !== key),
        })),

      resetMenuSettings: () =>
        set({
          menuOrder: defaultMenuOrder,
          openKeys: [],
          hiddenItems: [],
          editMode: false,
        }),

      reorderMenuItem: (fromIndex, toIndex) =>
        set((state) => {
          const newOrder = [...state.menuOrder];
          const [movedItem] = newOrder.splice(fromIndex, 1);
          newOrder.splice(toIndex, 0, movedItem);
          
          // Обновляем order для всех элементов
          return {
            menuOrder: newOrder.map((item, index) => ({
              ...item,
              order: index,
            })),
          };
        }),

      loadMenuSettingsFromServer: (order: MenuItemOrder[]) =>
        set({
          menuOrder: order && order.length > 0 ? order : defaultMenuOrder,
        }),
    }),
    {
      name: 'workernet-menu-settings', // Уникальное имя для localStorage
    }
  )
);

